import { useCallback, useEffect, useState } from 'react'

import { ATTEMPT_LABEL, useAdminLine } from '../../lib/adminLine'
import type { LineAttempt, LineRegistration, MenuKind, Overview } from '../../lib/adminLine'
import { formatUtcAsThai, nowThai } from '../../lib/datetime'
import { canEdit } from '../../lib/nav'
import { errorText } from '../../lib/offsite'
import { Pager } from '../../lib/Pager'
import { SortBar, SortTh, useSort } from '../../lib/Sort'
import { QrCode } from '../../lib/QrCode'
import { useAuthedSession } from '../../lib/SessionContext'
/* ใช้ชิ้นส่วนเดิมจากหน้า offsite แทนที่จะเขียนกล่องยืนยันขึ้นใหม่ —
   งานเดียวกัน (ทำสิ่งที่ย้อนไม่ได้ + บังคับกรอกเหตุผล) และการมีกล่องยืนยันสองแบบ
   ในระบบเดียวคือทางที่ทำให้วันหนึ่งมันเลิกเหมือนกัน */
import { ReasonPrompt, SectionHead, ViewerNote } from '../offsite/parts'

const DAY_CHOICES = [7, 30, 90, 365]

function StatusChip({ status }: { status: LineRegistration['status'] }) {
  return (
    <span className={status === 'active' ? 'chip chip--ok' : 'chip chip--off'}>
      {status === 'active' ? 'ผูกอยู่' : 'ยกเลิกแล้ว'}
    </span>
  )
}

export default function AdminLine() {
  const session = useAuthedSession()
  const line = useAdminLine()
  const writable = canEdit(session)

  return (
    <>
      <h1>LINE OA</h1>
      {!writable && <ViewerNote />}
      <OaPanel api={line} />
      <RegistrationTable api={line} writable={writable} />
      <AttemptTable api={line} />
    </>
  )
}

// ── ก. QR + ข้อมูล OA + โควตา + สถานะเมนู ───────────────────────────────────

function OaPanel({ api }: { api: ReturnType<typeof useAdminLine> }) {
  const [data, setData] = useState<Overview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadedAt, setLoadedAt] = useState<string | null>(null)

  const load = useCallback(
    async (refresh = false) => {
      setLoading(true)
      try {
        setData(await api.overview(refresh))
        setLoadedAt(nowThai())
        setError(null)
      } catch (err) {
        setError(errorText(err))
      } finally {
        setLoading(false)
      }
    },
    [api],
  )

  useEffect(() => {
    void load()
  }, [load])

  const oa = data?.oa
  const quota = data?.quota
  const menus = data?.menus

  return (
    <section className="card">
      <SectionHead
        title="บัญชี LINE OA"
        meta={loadedAt ? `ข้อมูลเมื่อ ${loadedAt.slice(11, 16)} (เวลาไทย)` : undefined}
        onReload={() => void load(true)}
        loading={loading}
      />

      {error && <p className="error">โหลดข้อมูลไม่สำเร็จ — {error}</p>}
      {!data && !error && <p className="meta">กำลังโหลด…</p>}

      {oa && !oa.ok && <p className="error">อ่านข้อมูล OA จาก LINE ไม่ได้ — {oa.error}</p>}

      {oa?.ok && (
        <div className="oa">
          <div className="oa__qr">
            {oa.addFriendUrl ? (
              <QrCode value={oa.addFriendUrl} size={190} filename={`line-qr-${oa.basicId ?? 'oa'}`} />
            ) : (
              <p className="meta">LINE ไม่ได้ส่ง basic id มา — สร้าง QR ไม่ได้</p>
            )}
          </div>

          <div className="oa__info">
            <dl>
              <dt>ชื่อ OA</dt>
              <dd>{oa.displayName ?? '-'}</dd>
              <dt>ID</dt>
              {/* พิมพ์เอาได้ตรง ๆ เผื่อสแกนไม่ติด — ค้นใน LINE ด้วย id นี้ก็เจอ */}
              <dd className="mono">{oa.basicId ?? '-'}</dd>
              <dt>ลิงก์เพิ่มเพื่อน</dt>
              <dd>
                {oa.addFriendUrl ? (
                  <a href={oa.addFriendUrl} target="_blank" rel="noreferrer" className="mono">
                    {oa.addFriendUrl}
                  </a>
                ) : (
                  '-'
                )}
              </dd>
              <dt>โหมดตอบแชท</dt>
              <dd>
                {oa.chatMode === 'bot' ? (
                  <span className="chip chip--ok">bot (ระบบตอบ)</span>
                ) : (
                  <span className="chip chip--warn">{oa.chatMode ?? '-'}</span>
                )}
              </dd>
              <dt>โควตาเดือนนี้</dt>
              <dd>
                {!quota?.ok
                  ? '-'
                  : quota.type === 'limited'
                    ? `ส่งไป ${quota.used.toLocaleString('th-TH')} จาก ${(quota.limit ?? 0).toLocaleString('th-TH')} ข้อความ`
                    : `ส่งไป ${quota.used.toLocaleString('th-TH')} ข้อความ (ไม่จำกัด)`}
              </dd>
            </dl>

            {/* ⚠️ เขียนไว้ให้เห็นชัด ไม่ใช่ซ่อนใน tooltip — ไม่งั้นจะมีคนแจ้งว่า
                "QR ของอีกบริษัทผิด" ทุกครั้งที่มีคนใหม่มาเปิดหน้านี้ */}
            <p className="meta">
              OA และ QR นี้ <strong>ใช้ร่วมกันทุกบริษัท</strong> เพราะมี Messaging API channel
              เดียว · ที่แยกตามบริษัทคือ LINE Login/LIFF และตารางลงทะเบียนที่อยู่คนละฐานข้อมูล
            </p>

            {oa.chatMode !== 'bot' && (
              <p className="warn">
                โหมดตอบแชทไม่ได้เป็น <strong>bot</strong> — LINE จะไม่ส่ง webhook มาให้ระบบ
                แปลว่าคำว่า “ลาออก” ในแชทจะไม่ทำงาน แก้ที่ LINE OA Manager → การตอบกลับ
              </p>
            )}
          </div>
        </div>
      )}

      {menus && <MenuStatusBlock menus={menus} />}
    </section>
  )
}

function MenuStatusBlock({ menus }: { menus: NonNullable<Overview['menus']> }) {
  if (!menus.ok) return <p className="error">อ่านสถานะเมนูไม่ได้ — {menus.error}</p>

  const missing = menus.menus.filter((m) => !m.deployed)

  return (
    <>
      <h3>rich menu</h3>

      {!menus.defaultIsRegister && (
        <p className="error">
          <strong>default ของ OA ไม่ใช่เมนู “ลงทะเบียน”</strong> — คนที่เพิ่งกดเพิ่มเพื่อนจะไม่เห็น
          ปุ่มลงทะเบียน เกิดได้จากการไปกดแก้เมนูใน LINE OA Manager ซึ่งทับค่าที่ตั้งจาก API
          แก้ด้วย <code>npm run richmenu -- deploy register &lt;บริษัท&gt;</code>
        </p>
      )}

      {missing.length > 0 && (
        <p className="warn">
          ยังไม่ได้ deploy เมนู: <strong>{missing.map((m) => m.kind).join(', ')}</strong> — เมนูที่หาย
          ทำให้คนกลุ่มนั้นค้างอยู่ที่เมนูเดิมโดยไม่มีข้อความแจ้งอะไรเลย
        </p>
      )}

      {menus.duplicatedNames.length > 0 && (
        <p className="error">
          <strong>มีเมนูชื่อซ้ำ:</strong> {menus.duplicatedNames.join(', ')} — ระบบหาเมนูจาก
          “ชื่อ” เจอสองอันแล้วจะผูกอันไหนก็ไม่แน่นอน ต้องลบตัวที่ไม่ใช้ทิ้ง
        </p>
      )}

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>เมนู</th>
              <th>ใครเห็น</th>
              <th>ขนาด</th>
              <th>แถบล่าง</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {menus.menus.map((m) => (
              <MenuRow key={m.kind} menu={m} />
            ))}
          </tbody>
        </table>
      </div>

      {menus.otherCount > 0 && (
        <p className="meta">
          มีเมนูอื่นในช่องนี้อีก {menus.otherCount} อันที่ไม่ใช่ของบริษัทนี้ (ของบริษัทอื่นหรือของเก่าที่ยังไม่ได้ลบ)
        </p>
      )}

      <p className="meta">
        หน้านี้ <strong>อ่านอย่างเดียว</strong> — การ deploy/sync เมนูมีผลกับทุกคนใน OA ทันทีที่กด
        และย้อนกลับด้วยการกดซ้ำไม่ได้ จึงยังให้ทำที่คำสั่ง <code>npm run richmenu</code> เท่านั้น
      </p>
    </>
  )
}

const WHO: Record<MenuKind['kind'], string> = {
  register: 'คนที่ยังไม่ลงทะเบียน (default ของ OA)',
  main: 'ลงทะเบียนแล้ว + มีสิทธิ์ลงเวลา',
  basic: 'ลงทะเบียนแล้ว แต่ไม่มีสิทธิ์ลงเวลา',
}

function MenuRow({ menu }: { menu: MenuKind }) {
  return (
    <tr className={menu.deployed ? undefined : 'off'}>
      <td className="nowrap">
        <strong>{menu.kind}</strong>
        {menu.isDefault && <div className="meta">เป็น default ของ OA</div>}
      </td>
      <td>{WHO[menu.kind]}</td>
      <td className="nowrap">{menu.size ?? '-'}</td>
      <td className="nowrap">{menu.chatBarText ?? '-'}</td>
      <td>
        {menu.deployed ? (
          <span className="chip chip--ok">มีแล้ว</span>
        ) : (
          <span className="chip chip--off">ยังไม่มี</span>
        )}
        {menu.duplicated && <span className="chip chip--warn">ชื่อซ้ำ</span>}
      </td>
    </tr>
  )
}

// ── ข. การผูกบัญชี ───────────────────────────────────────────────────────────

function RegistrationTable({
  api,
  writable,
}: {
  api: ReturnType<typeof useAdminLine>
  writable: boolean
}) {
  const [rows, setRows] = useState<LineRegistration[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const sort = useSort([{ key: 'status', dir: 'asc' }, { key: 'registeredAt', dir: 'desc' }], () => setPage(1))
  const [status, setStatus] = useState<'active' | 'revoked' | 'all'>('active')
  /** คำที่พิมพ์อยู่ vs คำที่ยิงไปแล้ว — แยกกันเพื่อไม่ให้ยิง API ทุกตัวอักษร */
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<LineRegistration | null>(null)
  const [loadedAt, setLoadedAt] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.listRegistrations({ status, q, page, pageSize, sort: sort.param })
      setRows(res.registrations)
      setTotal(res.total)
      setLoadedAt(nowThai())
      setError(null)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [api, status, q, page, pageSize, sort.param])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section className="card">
      <SectionHead
        title="การผูกบัญชี"
        meta={loadedAt ? `ข้อมูลเมื่อ ${loadedAt.slice(11, 16)} (เวลาไทย)` : undefined}
        onReload={() => void load()}
        loading={loading}
      />

      <form
        className="toolbar"
        onSubmit={(e) => {
          e.preventDefault()
          // เปลี่ยนคำค้นต้องกลับหน้า 1 — ไม่งั้นค้างหน้า 8 ของผลเดิมแล้วเห็นตารางว่าง
          setPage(1)
          setQ(term.trim())
        }}
      >
        <label>
          ค้นหา
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="รหัสพนักงาน / ชื่อ / ชื่อใน LINE"
          />
        </label>
        <label>
          สถานะ
          <select
            value={status}
            onChange={(e) => {
              setPage(1)
              setStatus(e.target.value as typeof status)
            }}
          >
            <option value="active">ผูกอยู่</option>
            <option value="revoked">ยกเลิกแล้ว</option>
            <option value="all">ทั้งหมด</option>
          </select>
        </label>
        <button type="submit" disabled={loading}>
          ค้นหา
        </button>
        {q !== '' && (
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setTerm('')
              setQ('')
              setPage(1)
            }}
          >
            ล้างคำค้น
          </button>
        )}
      </form>

      {error && <p className="error">โหลดข้อมูลไม่สำเร็จ — {error}</p>}
      {notice && <p className="ok">{notice}</p>}

      {revoking && (
        <ReasonPrompt
          title="ปลดการผูกบัญชี LINE"
          detail={`${revoking.empCode} · ${revoking.empName ?? '-'} · LINE: ${revoking.lineDisplayName ?? '-'}`}
          confirmLabel="ปลดการผูก"
          placeholder="เช่น พนักงานลาออก / ทำโทรศัพท์หาย"
          onConfirm={async (reason) => {
            await api.revoke(revoking.lineUserId, reason)
            setRevoking(null)
            setNotice(
              `ปลดการผูกของ ${revoking.empCode} แล้ว — เมนูของบัญชีนั้นกลับไปเป็นปุ่มลงทะเบียน ` +
                '(แอป LINE จะวาดเมนูใหม่ตอนเข้าห้องแชทครั้งถัดไป)',
            )
            await load()
          }}
          onClose={() => setRevoking(null)}
        />
      )}

      {rows === null ? (
        <p className="meta">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <p className="meta">{q ? `ไม่พบรายการที่ตรงกับ “${q}”` : 'ยังไม่มีใครผูกบัญชี'}</p>
      ) : (
        <>
          <SortBar
            sort={sort.sort}
            labels={{ empCode: 'พนักงาน', lineDisplayName: 'บัญชี LINE', registeredAt: 'ลงทะเบียนเมื่อ', status: 'สถานะ' }}
            onClear={() => sort.setSort([])}
          />
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <SortTh label="พนักงาน" sortKey="empCode" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="บัญชี LINE" sortKey="lineDisplayName" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="ลงทะเบียนเมื่อ" sortKey="registeredAt" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                  <SortTh label="สถานะ" sortKey="status" sort={sort.sort} onToggle={sort.toggle} />
                  {writable && <th>จัดการ</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={r.status === 'active' ? undefined : 'off'}>
                    <td>
                      <strong>{r.empCode}</strong> {r.empName ?? ''}
                      <div className="meta">
                        {[r.department, r.position].filter(Boolean).join(' · ') || '-'}
                      </div>
                    </td>
                    <td>
                      {r.lineDisplayName ?? <span className="meta">ไม่ทราบชื่อ</span>}
                      {/* 1 คนผูกได้หลาย LINE โดยตั้งใจ (DDL 001) — บอกให้เห็น ไม่ใช่ซ่อน
                          ไม่งั้นคนจะรายงานว่า "ทำไมชื่อซ้ำในตาราง" */}
                      {r.status === 'active' && r.activeAccounts > 1 && (
                        <div className="meta">คนนี้ผูกไว้ {r.activeAccounts} บัญชี</div>
                      )}
                      <div className="meta mono">{r.lineUserId}</div>
                    </td>
                    <td className="nowrap">{formatUtcAsThai(r.registeredAt)}</td>
                    <td>
                      <StatusChip status={r.status} />
                      {r.status === 'revoked' && (
                        <div className="meta">
                          {formatUtcAsThai(r.revokedAt)}
                          {r.revokeReason ? ` · ${r.revokeReason}` : ''}
                          {/* ไม่มี revokedBy = ผู้ใช้ยกเลิกเอง (หรือ DB ยังไม่ได้รัน 008) */}
                          {r.revokedBy ? ` · โดย ${r.revokedBy}` : ''}
                        </div>
                      )}
                    </td>
                    {writable && (
                      <td>
                        {r.status === 'active' && (
                          <button
                            type="button"
                            className="ghost tiny"
                            onClick={() => setRevoking(r)}
                          >
                            ปลดการผูก
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Pager
        page={page}
        pageSize={pageSize}
        total={total}
        loading={loading}
        onPage={setPage}
        unit="รายการ"
        onPageSize={(n) => {
          setPageSize(n)
          setPage(1)
        }}
      />
    </section>
  )
}

// ── ค. ความพยายามลงทะเบียน ──────────────────────────────────────────────────

function AttemptTable({ api }: { api: ReturnType<typeof useAdminLine> }) {
  const [rows, setRows] = useState<LineAttempt[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [days, setDays] = useState(30)
  const sort = useSort([{ key: 'createdAt', dir: 'desc' }], () => setPage(1))
  const [failedOnly, setFailedOnly] = useState(false)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.listAttempts({ days, failedOnly, page, pageSize, sort: sort.param })
      setRows(res.attempts)
      setTotal(res.total)
      setError(null)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [api, days, failedOnly, page, pageSize, sort.param])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section className="card">
      <SectionHead
        title="ความพยายามลงทะเบียน"
        meta="ใช้ตอบคำถามว่า “ลงทะเบียนไม่ได้ เพราะอะไร” โดยไม่ต้องรอให้ผู้ใช้เล่า"
        onReload={() => void load()}
        loading={loading}
      />

      <form className="toolbar" onSubmit={(e) => e.preventDefault()}>
        <label>
          ย้อนหลัง
          <select
            value={days}
            onChange={(e) => {
              setPage(1)
              setDays(Number(e.target.value))
            }}
          >
            {DAY_CHOICES.map((d) => (
              <option key={d} value={d}>
                {d} วัน
              </option>
            ))}
          </select>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={failedOnly}
            onChange={(e) => {
              setPage(1)
              setFailedOnly(e.target.checked)
            }}
          />
          เฉพาะที่ไม่สำเร็จ
        </label>
      </form>

      {error && <p className="error">โหลดข้อมูลไม่สำเร็จ — {error}</p>}

      {rows === null ? (
        <p className="meta">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <p className="meta">
          ไม่มีการพยายามลงทะเบียนใน {days} วันที่ผ่านมา{failedOnly ? ' (ที่ไม่สำเร็จ)' : ''}
        </p>
      ) : (
        <>
          <SortBar
            sort={sort.sort}
            labels={{ createdAt: 'เมื่อ', empCode: 'รหัสที่กรอก', result: 'ผล' }}
            onClear={() => sort.setSort([])}
          />
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <SortTh label="เมื่อ" sortKey="createdAt" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                  <SortTh label="รหัสที่กรอก" sortKey="empCode" sort={sort.sort} onToggle={sort.toggle} />
                  <th className="nowrap">บัตร 4 ตัวท้าย</th>
                  <SortTh label="ผล" sortKey="result" sort={sort.sort} onToggle={sort.toggle} />
                  <th>รายละเอียด</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} className={a.result === 'ok' ? 'off' : undefined}>
                    <td className="nowrap">{formatUtcAsThai(a.createdAt)}</td>
                    <td className="mono">{a.empCode ?? '-'}</td>
                    <td className="mono">{a.citizenIdLast4 ?? '-'}</td>
                    <td>
                      <span className={a.result === 'ok' ? 'chip chip--ok' : 'chip chip--warn'}>
                        {ATTEMPT_LABEL[a.result] ?? a.result}
                      </span>
                    </td>
                    <td>{a.message ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Pager
        page={page}
        pageSize={pageSize}
        total={total}
        loading={loading}
        onPage={setPage}
        unit="ครั้ง"
        onPageSize={(n) => {
          setPageSize(n)
          setPage(1)
        }}
      />
    </section>
  )
}
